<script setup>
import { ref, computed } from 'vue'
import AppLayout from '@/layouts/AppLayout.vue'

const props = defineProps({
  estimation:       { type: Object,  default: () => ({}) },
  projet:           { type: Object,  default: () => ({}) },
  rapports:         { type: Array,   default: () => [] },
  rapports_by_date: { type: Object,  default: () => ({}) },
  calendar_start:   { type: String,  default: null },
  calendar_stop:    { type: String,  default: null },
  can_create:       { type: Boolean, default: false },
  can_export:       { type: Boolean, default: false },
  can_see_costs:    { type: Boolean, default: false },
  show_meteo:       { type: Boolean, default: false },
  current_user_id:  { type: Number,  default: null },
  new_rapport_url:  { type: String,  default: '/rapports/new' },
  compilation_url:  { type: String,  default: '/rapports/compilation' },
  export_url:       { type: String,  default: '#' },
  rapports_url:     { type: String,  default: '/rapports' },
})

// ── Mois courant — initialisé sur le dernier rapport ──────
const initDate = props.rapports.length
  ? new Date(props.rapports[0].date + 'T00:00:00')
  : new Date()
const currentYear  = ref(initDate.getFullYear())
const currentMonth = ref(initDate.getMonth()) // 0-indexed

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']
const DAYS_FR   = ['LUN','MAR','MER','JEU','VEN','SAM','DIM']

function prevMonth() {
  if (currentMonth.value === 0) { currentMonth.value = 11; currentYear.value-- }
  else currentMonth.value--
}
function nextMonth() {
  if (currentMonth.value === 11) { currentMonth.value = 0; currentYear.value++ }
  else currentMonth.value++
}

const monthLabel = computed(() => `${MONTHS_FR[currentMonth.value]} ${currentYear.value}`)

const todayStr = new Date().toLocaleDateString('sv') // 'YYYY-MM-DD' locale-safe

// ── Grille calendrier ─────────────────────────────────────
const calendarDays = computed(() => {
  const y = currentYear.value
  const m = currentMonth.value
  const firstDay    = new Date(y, m, 1)
  const daysInMonth = new Date(y, m + 1, 0).getDate()
  const startOffset = (firstDay.getDay() + 6) % 7  // lundi = 0
  const days = Array(startOffset).fill(null)
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(`${y}-${String(m + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`)
  }
  while (days.length % 7 !== 0) days.push(null)
  return days
})

// ── Popover ───────────────────────────────────────────────
const hoveredDate   = ref(null)
const popoverStyle  = ref({})
const popoverActive = ref(false)
let leaveTimer = null

function onCellEnter(dateStr, event) {
  if (!dateStr || !props.rapports_by_date[dateStr]) return
  clearTimeout(leaveTimer)
  hoveredDate.value = dateStr
  const rect = event.currentTarget.getBoundingClientRect()
  const left = Math.min(rect.left, window.innerWidth - 260)
  const top  = rect.bottom + 6
  popoverStyle.value = { position: 'fixed', top: `${top}px`, left: `${left}px`, zIndex: 9999 }
}
function onCellLeave() {
  leaveTimer = setTimeout(() => { if (!popoverActive.value) hoveredDate.value = null }, 130)
}
function onPopoverEnter() { popoverActive.value = true; clearTimeout(leaveTimer) }
function onPopoverLeave() { popoverActive.value = false; hoveredDate.value = null }

// ── Helpers ───────────────────────────────────────────────
function userHasRapport(dateStr) {
  return (props.rapports_by_date[dateStr] ?? []).some(r => r.utilisateur_id === props.current_user_id)
}
function newRapportUrl(dateStr) { return `${props.new_rapport_url}&date=${dateStr}` }
function compilationUrlForDate(dateStr) { return `${props.compilation_url}&debut=${dateStr}&fin=${dateStr}` }
function formatDay(dateStr) { return dateStr ? Number(dateStr.slice(8)) : '' }
function formatDateLabel(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
}

const CAD = new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 })
function fmt(val)     { return val == null ? '—' : CAD.format(val) }
function fmtProd(val) {
  if (val == null) return '—'
  return (val >= 0 ? '+' : '-') + CAD.format(Math.abs(val))
}
function prodClass(val) { return val > 0 ? 'text-tertiary' : val < 0 ? 'text-error' : 'text-outline' }

// ── KPI ───────────────────────────────────────────────────
const totalRapports   = computed(() => props.rapports.length)
const lastRapportDate = computed(() => props.rapports[0]?.date ?? null)
const periodLabel     = computed(() => {
  if (!props.calendar_start && !props.calendar_stop) return '—'
  return `${props.calendar_start ?? '?'} → ${props.calendar_stop ?? '?'}`
})

// ── Tri du tableau ────────────────────────────────────────
const sortKey = ref('date')
const sortDir = ref('desc')

function toggleSort(key) {
  if (sortKey.value === key) sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  else { sortKey.value = key; sortDir.value = 'asc' }
  currentPage.value = 1
}

const sortedRapports = computed(() => {
  const list = [...props.rapports]
  list.sort((a, b) => {
    let va = a[sortKey.value] ?? ''
    let vb = b[sortKey.value] ?? ''
    if (typeof va === 'number') return sortDir.value === 'asc' ? va - vb : vb - va
    return sortDir.value === 'asc'
      ? String(va).localeCompare(String(vb))
      : String(vb).localeCompare(String(va))
  })
  return list
})

// ── Pagination du tableau ─────────────────────────────────
const pageSize    = ref(25)
const currentPage = ref(1)

const totalPages = computed(() => Math.max(1, Math.ceil(sortedRapports.value.length / pageSize.value)))
const pagedRapports = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return sortedRapports.value.slice(start, start + pageSize.value)
})
const pageNumbers = computed(() => {
  const total = totalPages.value
  const cur   = currentPage.value
  const pages = new Set([1, total, cur, cur - 1, cur + 1].filter(p => p >= 1 && p <= total))
  const sorted = [...pages].sort((a, b) => a - b)
  // Ajouter des ellipses
  const result = []
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) result.push('…')
    result.push(p)
  })
  return result
})

function onPageSize() { currentPage.value = 1 }

// ── Classes partagées tableau ─────────────────────────────
const thCls    = 'px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-outline whitespace-nowrap'
const thSortCls = 'px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest whitespace-nowrap cursor-pointer select-none hover:text-on-surface transition-colors'
const tdCls    = 'px-4 py-3 text-sm text-on-surface whitespace-nowrap'
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="flex items-center gap-3">
        <a :href="projet.url" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-colors">{{ projet.code }}</a>
        <span class="text-outline">/</span>
        <a :href="estimation.url" class="font-mono text-sm font-bold text-primary hover:brightness-125 transition-colors">{{ estimation.code }}</a>
        <span class="text-outline">/</span>
        <span class="text-sm font-black uppercase tracking-widest text-on-surface">Calendrier</span>
      </div>
    </template>

    <div class="p-8 space-y-6">

      <!-- ── KPI strip ───────────────────────────────────── -->
      <div class="grid grid-cols-3 gap-4">
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 px-5 py-4">
          <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-1">Bordereaux</p>
          <p class="text-3xl font-black text-on-surface">{{ totalRapports }}</p>
        </div>
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 px-5 py-4">
          <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-1">Dernier bordereau</p>
          <p class="text-lg font-black text-on-surface font-mono">{{ lastRapportDate ?? '—' }}</p>
        </div>
        <div class="bg-surface-container rounded-xl border border-outline-variant/10 px-5 py-4">
          <p class="text-[10px] font-black uppercase tracking-widest text-outline mb-1">Période du projet</p>
          <p class="text-sm font-bold text-on-surface font-mono">{{ periodLabel }}</p>
        </div>
      </div>

      <!-- ── Calendrier ──────────────────────────────────── -->
      <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">

        <!-- Barre navigation mois -->
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
          <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Calendrier</h2>
          <div class="flex items-center gap-3">
            <button @click="prevMonth"
                    class="w-8 h-8 flex items-center justify-center rounded-md text-outline hover:text-on-surface hover:bg-surface-container-highest transition-all">
              <span class="material-symbols-outlined text-base">chevron_left</span>
            </button>
            <span class="text-sm font-black uppercase tracking-widest text-on-surface w-48 text-center select-none">{{ monthLabel }}</span>
            <button @click="nextMonth"
                    class="w-8 h-8 flex items-center justify-center rounded-md text-outline hover:text-on-surface hover:bg-surface-container-highest transition-all">
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>

        <div class="p-4">
          <!-- En-têtes jours -->
          <div class="grid grid-cols-7 mb-2">
            <div v-for="d in DAYS_FR" :key="d"
                 class="py-2 text-center text-[10px] font-black uppercase tracking-widest text-outline/60">
              {{ d }}
            </div>
          </div>

          <!-- Grille des jours -->
          <div class="grid grid-cols-7 gap-1.5">
            <template v-for="(dateStr, idx) in calendarDays" :key="idx">

              <!-- Cellule vide (padding de début/fin de mois) -->
              <div v-if="!dateStr" class="min-h-[96px] rounded-lg bg-surface-container-high/5" />

              <!-- Cellule active -->
              <div v-else
                   @mouseenter="onCellEnter(dateStr, $event)"
                   @mouseleave="onCellLeave"
                   :class="['min-h-[96px] rounded-lg p-2 flex flex-col gap-1 transition-all',
                     dateStr === todayStr
                       ? 'ring-1 ring-primary/40'
                       : '',
                     rapports_by_date[dateStr]
                       ? 'bg-surface-container-high hover:bg-surface-container-highest cursor-pointer'
                       : 'bg-surface-container-high/30 hover:bg-surface-container-high/60']">

                <!-- Numéro du jour -->
                <span :class="['text-xs font-black leading-none',
                  dateStr === todayStr ? 'text-primary' : 'text-on-surface/50']">
                  {{ formatDay(dateStr) }}
                </span>

                <!-- ── Cas : rapports existants ── -->
                <template v-if="rapports_by_date[dateStr]">

                  <!-- Pill compilation (si > 1 rapport) -->
                  <a v-if="rapports_by_date[dateStr].length > 1"
                     :href="compilationUrlForDate(dateStr)"
                     class="flex items-center gap-1 px-1.5 py-1 rounded bg-primary/15 hover:bg-primary/25 transition-colors group">
                    <span class="material-symbols-outlined text-[11px] text-primary">layers</span>
                    <span class="text-[9px] font-black uppercase tracking-widest text-primary leading-none truncate">
                      Compilation
                    </span>
                  </a>

                  <!-- Pills par rapport -->
                  <a v-for="r in rapports_by_date[dateStr]" :key="r.id"
                     :href="r.url"
                     class="flex items-center gap-1 px-1.5 py-1 rounded bg-tertiary/10 hover:bg-tertiary/20 transition-colors group">
                    <span class="material-symbols-outlined text-[11px] text-tertiary">check_circle</span>
                    <span class="text-[9px] font-bold text-tertiary leading-none truncate">{{ r.utilisateur_nom }}</span>
                  </a>

                  <!-- Ajouter (si pas encore soumis ce jour) -->
                  <a v-if="can_create && !userHasRapport(dateStr)"
                     :href="newRapportUrl(dateStr)"
                     class="flex items-center gap-0.5 mt-auto text-[9px] text-outline/50 hover:text-primary transition-colors">
                    <span class="material-symbols-outlined text-[11px]">add</span>
                    <span class="leading-none">Ajouter</span>
                  </a>
                </template>

                <!-- ── Cas : journée vide ── -->
                <template v-else-if="can_create">
                  <a :href="newRapportUrl(dateStr)"
                     class="flex items-center gap-0.5 mt-auto text-[9px] text-outline/30 hover:text-primary transition-colors">
                    <span class="material-symbols-outlined text-[11px]">add</span>
                    <span class="leading-none">Ajouter</span>
                  </a>
                </template>

              </div>
            </template>
          </div>
        </div>
      </div><!-- fin calendrier -->

      <!-- ══════════════════════════════════════════════════
           TABLEAU — liste complète avec tri + pagination
           ══════════════════════════════════════════════════ -->
      <div class="bg-surface-container rounded-xl border border-outline-variant/10 shadow-lg overflow-hidden">

        <!-- En-tête tableau -->
        <div class="px-6 py-4 border-b border-outline-variant/10 bg-surface-container-high/40 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <h2 class="text-[11px] font-black uppercase tracking-widest text-outline">Journal quotidien</h2>
            <span class="text-[10px] font-black text-outline/40 uppercase tracking-widest">
              {{ totalRapports }} bordereau{{ totalRapports > 1 ? 'x' : '' }}
            </span>
          </div>
          <div class="flex items-center gap-3">
            <!-- Taille de page -->
            <div class="flex items-center gap-2">
              <span class="text-[10px] font-black uppercase tracking-widest text-outline">Afficher</span>
              <select v-model="pageSize" @change="onPageSize"
                      class="bg-surface-container-highest border border-outline-variant/20 rounded px-2 py-1 text-xs text-on-surface focus:outline-none focus:ring-1 focus:ring-primary/30">
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
              </select>
              <span class="text-[10px] font-black uppercase tracking-widest text-outline">par page</span>
            </div>
            <!-- Export -->
            <a v-if="can_export && totalRapports > 0"
               :href="export_url"
               class="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface border border-outline-variant/20 rounded-md px-3 py-2 transition-all">
              <span class="material-symbols-outlined text-sm">download</span>Export XLSX
            </a>
          </div>
        </div>

        <div v-if="!rapports.length" class="px-6 py-12 text-center text-outline/40 text-sm italic">
          Aucun bordereau pour cette estimation.
        </div>

        <template v-else>
          <div class="overflow-x-auto">
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr class="bg-surface-container-high/30 border-b border-outline-variant/10">
                  <!-- Date — triable -->
                  <th :class="[thSortCls, sortKey === 'date' ? 'text-primary' : 'text-outline']"
                      @click="toggleSort('date')">
                    <span class="flex items-center gap-1">
                      Date
                      <span class="material-symbols-outlined text-xs">
                        {{ sortKey === 'date' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                      </span>
                    </span>
                  </th>
                  <!-- Utilisateur — triable -->
                  <th :class="[thSortCls, sortKey === 'nom_complet' ? 'text-primary' : 'text-outline']"
                      @click="toggleSort('nom_complet')">
                    <span class="flex items-center gap-1">
                      Utilisateur
                      <span class="material-symbols-outlined text-xs">
                        {{ sortKey === 'nom_complet' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                      </span>
                    </span>
                  </th>
                  <template v-if="can_see_costs">
                    <th :class="[thSortCls, sortKey === 'c_valeur_total' ? 'text-primary' : 'text-outline']"
                        @click="toggleSort('c_valeur_total')">
                      <span class="flex items-center gap-1">
                        Total Items
                        <span class="material-symbols-outlined text-xs">
                          {{ sortKey === 'c_valeur_total' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                        </span>
                      </span>
                    </th>
                    <th :class="[thSortCls, sortKey === 'c_valeur' ? 'text-primary' : 'text-outline']"
                        @click="toggleSort('c_valeur')">
                      <span class="flex items-center gap-1">
                        Items
                        <span class="material-symbols-outlined text-xs">
                          {{ sortKey === 'c_valeur' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                        </span>
                      </span>
                    </th>
                    <th :class="[thSortCls, sortKey === 'c_personnel' ? 'text-primary' : 'text-outline']"
                        @click="toggleSort('c_personnel')">
                      <span class="flex items-center gap-1">
                        Personnel
                        <span class="material-symbols-outlined text-xs">
                          {{ sortKey === 'c_personnel' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                        </span>
                      </span>
                    </th>
                    <th :class="[thSortCls, sortKey === 'c_equipement' ? 'text-primary' : 'text-outline']"
                        @click="toggleSort('c_equipement')">
                      <span class="flex items-center gap-1">
                        Équipements
                        <span class="material-symbols-outlined text-xs">
                          {{ sortKey === 'c_equipement' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                        </span>
                      </span>
                    </th>
                    <th :class="[thSortCls, sortKey === 'c_productivite' ? 'text-primary' : 'text-outline']"
                        @click="toggleSort('c_productivite')">
                      <span class="flex items-center gap-1">
                        Productivité
                        <span class="material-symbols-outlined text-xs">
                          {{ sortKey === 'c_productivite' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}
                        </span>
                      </span>
                    </th>
                  </template>
                  <th v-if="show_meteo" :class="thCls + ' text-center'">Météo</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outline-variant/10">
                <tr v-for="r in pagedRapports" :key="r.id"
                    class="hover:bg-surface-container-highest/20 transition-colors">
                  <td :class="tdCls">
                    <a :href="r.url" class="font-mono font-bold text-primary hover:brightness-125 transition-colors">
                      {{ r.date }}
                    </a>
                  </td>
                  <td :class="tdCls">{{ r.nom_complet }}</td>
                  <template v-if="can_see_costs">
                    <td :class="tdCls + ' font-mono'">{{ fmt(r.c_valeur_total) }}</td>
                    <td :class="tdCls + ' font-mono'">{{ fmt(r.c_valeur) }}</td>
                    <td :class="tdCls + ' font-mono'">{{ fmt(r.c_personnel) }}</td>
                    <td :class="tdCls + ' font-mono'">{{ fmt(r.c_equipement) }}</td>
                    <td :class="[tdCls, 'font-mono font-bold', prodClass(r.c_productivite)]">{{ fmtProd(r.c_productivite) }}</td>
                  </template>
                  <td v-if="show_meteo" :class="tdCls + ' text-center'">
                    <i :class="`wi ${r.meteo}`" style="font-size:1.2em"></i>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="px-6 py-4 border-t border-outline-variant/10 flex items-center justify-between">
            <span class="text-[10px] font-black uppercase tracking-widest text-outline">
              {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, totalRapports) }}
              sur {{ totalRapports }}
            </span>
            <div class="flex items-center gap-1">
              <button @click="currentPage--" :disabled="currentPage === 1"
                      class="w-8 h-8 flex items-center justify-center rounded-md text-outline hover:text-on-surface hover:bg-surface-container-high disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <span class="material-symbols-outlined text-base">chevron_left</span>
              </button>
              <template v-for="p in pageNumbers" :key="p">
                <span v-if="p === '…'" class="w-8 text-center text-outline text-xs">…</span>
                <button v-else @click="currentPage = p"
                        :class="['w-8 h-8 flex items-center justify-center rounded-md text-xs font-black transition-all',
                          currentPage === p
                            ? 'bg-primary/20 text-primary'
                            : 'text-outline hover:text-on-surface hover:bg-surface-container-high']">
                  {{ p }}
                </button>
              </template>
              <button @click="currentPage++" :disabled="currentPage === totalPages"
                      class="w-8 h-8 flex items-center justify-center rounded-md text-outline hover:text-on-surface hover:bg-surface-container-high disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <span class="material-symbols-outlined text-base">chevron_right</span>
              </button>
            </div>
          </div>
        </template>
      </div><!-- fin tableau -->

    </div><!-- fin p-8 -->

    <!-- ── Popover hover (Teleport body) ─────────────────── -->
    <Teleport to="body">
      <div v-if="hoveredDate && rapports_by_date[hoveredDate]"
           :style="popoverStyle"
           @mouseenter="onPopoverEnter"
           @mouseleave="onPopoverLeave"
           class="w-60 bg-surface-container-highest border border-outline-variant/20 rounded-xl shadow-2xl overflow-hidden">

        <!-- En-tête -->
        <div class="px-4 py-3 border-b border-outline-variant/10 bg-surface-container-high/60">
          <p class="text-[10px] font-black uppercase tracking-widest text-outline capitalize">
            {{ formatDateLabel(hoveredDate) }}
          </p>
        </div>

        <!-- Liste rapports scrollable -->
        <div class="max-h-52 overflow-y-auto divide-y divide-outline-variant/10">
          <a v-for="r in rapports_by_date[hoveredDate]" :key="r.id"
             :href="r.url"
             class="flex items-center gap-2.5 px-4 py-3 hover:bg-surface-container-high transition-colors group">
            <span class="material-symbols-outlined text-sm text-tertiary">check_circle</span>
            <span class="text-sm font-bold text-on-surface group-hover:text-primary transition-colors">{{ r.utilisateur_nom }}</span>
          </a>
        </div>

        <!-- Actions -->
        <div class="px-4 py-3 border-t border-outline-variant/10 space-y-2">
          <a v-if="rapports_by_date[hoveredDate].length > 1"
             :href="compilationUrlForDate(hoveredDate)"
             class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary hover:brightness-125 transition-all">
            <span class="material-symbols-outlined text-sm">layers</span>Voir la compilation
          </a>
          <a v-if="can_create && !userHasRapport(hoveredDate)"
             :href="newRapportUrl(hoveredDate)"
             class="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-outline hover:text-on-surface transition-colors">
            <span class="material-symbols-outlined text-sm">add_circle</span>Ajouter un rapport
          </a>
        </div>
      </div>
    </Teleport>

  </AppLayout>
</template>
